import { useState, useRef, useEffect, ReactNode } from 'react';
import { Box, Button, Container, Stack, SvgIcon, Typography, Collapse } from '@mui/material';
import ArrowRightIcon from '@untitled-ui/icons-react/build/esm/ArrowRight';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';
import { HomeHero } from 'src/sections/home/home-hero';
import { HomeGoogleReviews } from 'src/sections/home/home-google-reviews';
import { RouterLink } from 'src/components/router-link';
import { paths } from 'src/paths';
import { projects, projectCategories } from 'src/data/projects';
import type { Page as PageType } from 'src/types/page';

const services = [
  { num: '01', title: 'Branding', body: 'Identity systems that signal who you are and why it matters.' },
  { num: '02', title: 'Web Development', body: 'Fast, modern websites built to convert and last.' },
  { num: '03', title: 'Marketing', body: 'Campaigns that move metrics, not just impressions.' },
  { num: '04', title: 'SEO', body: 'Long-term organic growth grounded in real strategy.' },
  { num: '05', title: 'Content', body: 'Video, photo, and copy that actually gets watched.' },
  { num: '06', title: 'Paid Ads', body: 'Performance creative across Meta, Google, and TikTok.' }
];

const faqs = [
  {
    q: 'What kinds of projects do you take on?',
    a: 'Anything that helps a brand grow online — websites, branding, marketing campaigns, SEO, paid ads, video, and content. We work with small businesses, startups, and growing teams.'
  },
  {
    q: 'How long does a typical project take?',
    a: 'Most projects run 2–8 weeks depending on scope. Brand and website builds typically take 4–6 weeks; ongoing marketing and content work runs month-to-month.'
  },
  {
    q: 'How much does it cost?',
    a: 'Every project is different. We scope, quote, and only move forward once you\'re confident in the price. Most engagements start around $2k for one-off projects and $1.5k/mo for retainers.'
  },
  {
    q: 'Do you work with clients outside Katy, TX?',
    a: 'Absolutely. We\'re based in Katy but work with brands across the U.S. — most of our process happens over Slack, Loom, and Zoom.'
  },
  {
    q: 'How do we get started?',
    a: 'Send us a note. We\'ll set up a quick intro call to learn about your project and follow up with a proposal within a couple of business days.'
  }
];

const FaqItem = ({ q, a, i, isOpen, onToggle }: { q: string; a: string; i: number; isOpen: boolean; onToggle: () => void }) => (
  <Box
    onClick={onToggle}
    sx={{
      cursor: 'pointer',
      position: 'relative',
      px: { xs: 3, md: 4 },
      py: { xs: 3, md: 3.5 },
      borderRadius: 3,
      background: isOpen
        ? 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)'
        : '#ffffff',
      border: '1px solid',
      borderColor: isOpen ? '#2563eb' : '#e4e4e7',
      boxShadow: isOpen
        ? '0 20px 40px -16px rgba(37, 99, 235, 0.18), 0 0 0 4px rgba(37, 99, 235, 0.06)'
        : '0 1px 2px rgba(15, 23, 42, 0.04)',
      transition: 'all 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
      '&:hover': {
        borderColor: isOpen ? '#2563eb' : '#a1a1aa',
        transform: isOpen ? 'none' : 'translateY(-1px)',
        boxShadow: isOpen
          ? '0 20px 40px -16px rgba(37, 99, 235, 0.18), 0 0 0 4px rgba(37, 99, 235, 0.06)'
          : '0 4px 12px rgba(15, 23, 42, 0.06)'
      }
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      spacing={{ xs: 2, md: 3 }}
    >
      <Typography
        sx={{
          fontFamily: '"Roboto Mono", monospace',
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 0.8,
          color: isOpen ? '#2563eb' : '#a1a1aa',
          minWidth: 32,
          transition: 'color 0.2s ease'
        }}
      >
        {String(i + 1).padStart(2, '0')}
      </Typography>
      <Typography
        sx={{
          fontFamily: '"Inter", sans-serif',
          fontSize: { xs: 16, md: 18 },
          fontWeight: 600,
          letterSpacing: '-0.015em',
          color: '#0a0a0b',
          flex: 1,
          textAlign: 'left'
        }}
      >
        {q}
      </Typography>
      <Box
        sx={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: isOpen ? '#2563eb' : '#fafafa',
          color: isOpen ? '#ffffff' : '#0a0a0b',
          border: isOpen ? 'none' : '1px solid #e4e4e7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
          transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
          fontSize: 20,
          fontWeight: 300,
          lineHeight: 1,
          boxShadow: isOpen ? '0 6px 16px rgba(37, 99, 235, 0.3)' : 'none'
        }}
      >
        +
      </Box>
    </Stack>
    <Collapse in={isOpen} timeout={300}>
      <Box
        sx={{
          mt: 2.5,
          pt: 2.5,
          pl: { xs: 0, md: 7 },
          borderTop: '1px solid #f4f4f5'
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: { xs: 14, md: 15.5 },
            fontWeight: 400,
            lineHeight: 1.65,
            color: '#52525b',
            textAlign: 'left'
          }}
        >
          {a}
        </Typography>
      </Box>
    </Collapse>
  </Box>
);

const Reveal = ({ children, delay = 0 }: { children: ReactNode; delay?: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // threshold 0: fire as soon as ANY pixel enters the viewport.
        // A non-zero threshold can never be met by an element taller
        // than the viewport (intersectionRatio caps at viewportH/elH),
        // which made tall mobile sections stay invisible. Once shown,
        // disconnect so it never hides again.
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -80px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.98)',
        filter: visible ? 'blur(0)' : 'blur(6px)',
        transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, filter 0.7s ease ${delay}s`,
        willChange: 'opacity, transform, filter'
      }}
    >
      {children}
    </Box>
  );
};

const SectionLabel = ({ children }: { children: string }) => (
  <Stack direction="row" alignItems="center" spacing={1.25}>
    <Box sx={{ width: 24, height: 1, background: '#0a0a0b' }} />
    <Typography
      sx={{
        fontFamily: '"Roboto Mono", monospace',
        fontSize: 11,
        fontWeight: 500,
        color: '#0a0a0b',
        letterSpacing: 1.4,
        textTransform: 'uppercase'
      }}
    >
      {children}
    </Typography>
  </Stack>
);

const sectionShellSx = {
  py: { xs: 12, md: 18 },
  borderTop: '1px solid #f4f4f5'
} as const;

const sectionTitleSx = {
  fontFamily: '"Inter", sans-serif',
  fontSize: { xs: '36px', md: '60px' },
  fontWeight: 700,
  lineHeight: 1.04,
  letterSpacing: '-0.035em',
  color: '#0a0a0b'
} as const;

const Page: PageType = () => {
  usePageView();

  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [activeCategory, setActiveCategory] = useState(projectCategories[0]);
  const filteredProjects = projects.filter((p) => p.category === activeCategory);

  return (
    <>
      <Seo
        title="Mystic Media Film | Houston Video Production & Marketing"
        description="Houston video production, web design, and marketing agency. Mystic Media Film helps ambitious brands grow with cinematic content and high-converting sites."
        canonicalUrl="https://mysticmediafilm.com/"
      />
      <main style={{ background: '#ffffff' }}>
        <HomeHero />

        {/* Services */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="lg">
            <Reveal>
            <Stack spacing={{ xs: 5, md: 7 }}>
              <Stack spacing={2.5}>
                <SectionLabel>Services</SectionLabel>
                <Typography sx={sectionTitleSx}>
                  Everything you need to grow,{' '}
                  <Box component="span" sx={{ color: '#2563eb' }}>under one roof.</Box>
                </Typography>
              </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                    gap: { xs: 1.5, md: 2 }
                  }}
                >
                  {services.map((s, i) => (
                    <Box
                      key={s.title}
                      sx={{
                        position: 'relative',
                        p: { xs: 3, md: 3.5 },
                        borderRadius: 3,
                        background: i === 0 ? '#0a0a0b' : '#fafafa',
                        border: '1px solid',
                        borderColor: i === 0 ? '#0a0a0b' : '#e4e4e7',
                        color: i === 0 ? '#ffffff' : '#0a0a0b',
                        overflow: 'hidden',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          borderColor: i === 0 ? '#0a0a0b' : '#0a0a0b',
                          boxShadow: i === 0
                            ? '0 14px 30px rgba(37, 99, 235, 0.25)'
                            : '0 10px 24px rgba(15, 23, 42, 0.07)',
                          '& .arrow': { transform: 'translate(2px, -2px)' }
                        }
                      }}
                    >
                      {i === 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '-40%',
                            right: '-20%',
                            width: 320,
                            height: 320,
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(37, 99, 235, 0.5) 0%, transparent 65%)',
                            filter: 'blur(50px)',
                            pointerEvents: 'none'
                          }}
                        />
                      )}

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 4, position: 'relative', zIndex: 1 }}
                      >
                        <Typography
                          sx={{
                            fontFamily: '"Roboto Mono", monospace',
                            fontSize: 11,
                            fontWeight: 500,
                            letterSpacing: 0.6,
                            color: i === 0 ? 'rgba(255,255,255,0.55)' : '#a1a1aa'
                          }}
                        >
                          {s.num} / 06
                        </Typography>
                        <Box
                          className="arrow"
                          component="svg"
                          viewBox="0 0 24 24"
                          sx={{
                            width: 18,
                            height: 18,
                            transition: 'transform 0.2s ease',
                            color: i === 0 ? 'rgba(255,255,255,0.7)' : '#a1a1aa'
                          }}
                        >
                          <path
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M7 17 L17 7 M9 7 L17 7 L17 15"
                          />
                        </Box>
                      </Stack>

                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: { xs: 24, md: 28 },
                          fontWeight: 600,
                          lineHeight: 1.05,
                          letterSpacing: '-0.03em',
                          color: i === 0 ? '#ffffff' : '#0a0a0b',
                          mb: 1.5,
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        {s.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: 14,
                          fontWeight: 400,
                          lineHeight: 1.5,
                          color: i === 0 ? 'rgba(255,255,255,0.65)' : '#52525b',
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        {s.body}
                      </Typography>
                    </Box>
                  ))}
                </Box>
            </Stack>
            </Reveal>
          </Container>
        </Box>

        {/* Past Projects */}
        <Box
          sx={{
            position: 'relative',
            background: '#fafafa',
            borderTop: '1px solid #f4f4f5',
            py: { xs: 12, md: 18 },
            overflow: 'hidden'
          }}
        >
          {/* Soft glow accents */}
          <Box
            sx={{
              position: 'absolute',
              top: '-20%',
              left: '-10%',
              width: 600,
              height: 600,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, transparent 60%)',
              filter: 'blur(80px)',
              pointerEvents: 'none'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '-10%',
              right: '-10%',
              width: 500,
              height: 500,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(6, 182, 212, 0.07) 0%, transparent 60%)',
              filter: 'blur(80px)',
              pointerEvents: 'none'
            }}
          />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Reveal>
            <Stack spacing={{ xs: 5, md: 7 }}>
              {/* Centered header */}
              <Stack spacing={{ xs: 3, md: 3.5 }} alignItems="center" sx={{ textAlign: 'center' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    pl: 0.75,
                    pr: 1.75,
                    py: 0.5,
                    borderRadius: 999,
                    background: '#ffffff',
                    border: '1px solid #e4e4e7',
                    boxShadow: '0 4px 14px -4px rgba(15, 23, 42, 0.06)'
                  }}
                >
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #2563eb 0%, #06b6d4 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box
                      component="svg"
                      viewBox="0 0 24 24"
                      sx={{ width: 10, height: 10, color: '#ffffff' }}
                    >
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M7 17 L17 7 M9 7 L17 7 L17 15"
                      />
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: '"Roboto Mono", monospace',
                      fontSize: 10.5,
                      fontWeight: 500,
                      letterSpacing: 1.1,
                      textTransform: 'uppercase',
                      color: '#0a0a0b'
                    }}
                  >
                    Our Work
                  </Typography>
                </Stack>

                <Stack alignItems="center" spacing={0.5}>
                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: { xs: '40px', md: '64px' },
                      fontWeight: 700,
                      lineHeight: 1.05,
                      letterSpacing: '-0.035em',
                      color: '#0a0a0b'
                    }}
                  >
                    Past projects,
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: { xs: '40px', md: '64px' },
                      fontWeight: 700,
                      lineHeight: 1.05,
                      letterSpacing: '-0.035em',
                      fontStyle: 'italic',
                      background: 'linear-gradient(135deg, #2563eb 0%, #06b6d4 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    real results.
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: { xs: 15, md: 17 },
                    fontWeight: 400,
                    lineHeight: 1.55,
                    color: '#52525b',
                    maxWidth: 620
                  }}
                >
                  We design and build modern, fast, and beautiful work that turns visitors into customers. Here are a few we&apos;ve put into the world.
                </Typography>
              </Stack>

              {/* Centered category filter */}
              <Stack
                direction="row"
                justifyContent="center"
                sx={{
                  flexWrap: 'wrap',
                  gap: { xs: 1, md: 1.25 }
                }}
              >
                {projectCategories.map((cat) => {
                  const active = cat === activeCategory;
                  const count = projects.filter((p) => p.category === cat).length;
                  return (
                    <Stack
                      key={cat}
                      direction="row"
                      alignItems="center"
                      spacing={0.75}
                      onClick={() => setActiveCategory(cat)}
                      sx={{
                        cursor: 'pointer',
                        px: 1.75,
                        py: 0.75,
                        borderRadius: 999,
                        border: '1px solid',
                        borderColor: active ? '#0a0a0b' : '#e4e4e7',
                        background: active ? '#0a0a0b' : '#ffffff',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: active ? '#0a0a0b' : '#a1a1aa',
                          background: active ? '#0a0a0b' : '#f4f4f5'
                        }
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: { xs: 13, md: 14 },
                          fontWeight: active ? 600 : 500,
                          letterSpacing: '-0.005em',
                          color: active ? '#ffffff' : '#52525b',
                          transition: 'color 0.2s ease'
                        }}
                      >
                        {cat}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Roboto Mono", monospace',
                          fontSize: 10,
                          fontWeight: 500,
                          letterSpacing: 0.5,
                          color: active ? '#60a5fa' : '#a1a1aa',
                          transition: 'color 0.2s ease'
                        }}
                      >
                        {String(count).padStart(2, '0')}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>

              {/* Project grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: { xs: 2, md: 2.5 }
                }}
              >
                {filteredProjects.map((p, i) => {
                  const project = p as any;
                  const hasImage = !!project.image;
                  const isYouTubeEmbed = !hasImage && typeof p.src === 'string' && p.src.includes('youtube.com/embed/');
                  const tags: string[] = project.tags || [p.category];
                  return (
                    <Box
                      key={`${activeCategory}-${i}`}
                      onClick={(e) => {
                        // Clicks landing inside the iframe (YouTube player) play the video inline
                        // and don't bubble up to React, so this only fires for clicks outside the media.
                        if (!p.src.startsWith('http')) return;
                        const ytMatch = p.src.match(/youtube\.com\/embed\/([^?/&]+)/);
                        if (ytMatch) {
                          const startMatch = p.src.match(/[?&]start=(\d+)/);
                          const watchUrl = `https://www.youtube.com/watch?v=${ytMatch[1]}${startMatch ? `&t=${startMatch[1]}s` : ''}`;
                          window.open(watchUrl, '_blank', 'noopener,noreferrer');
                          return;
                        }
                        window.open(p.src, '_blank', 'noopener,noreferrer');
                      }}
                      sx={{
                        position: 'relative',
                        borderRadius: 3,
                        overflow: 'hidden',
                        background: '#ffffff',
                        border: '1px solid #e4e4e7',
                        transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          borderColor: '#2563eb',
                          boxShadow: '0 32px 64px -20px rgba(15, 23, 42, 0.22), 0 0 0 1px rgba(37, 99, 235, 0.15)',
                          '& .media': { transform: 'scale(1.04)' },
                          '& .visit-link': { color: '#2563eb' },
                          '& .visit-arrow': { transform: 'translate(3px, -3px)', color: '#2563eb' }
                        }
                      }}
                    >
                      {/* Image */}
                      <Box
                        sx={{
                          position: 'relative',
                          aspectRatio: '16 / 10',
                          overflow: 'hidden',
                          background: '#0a0a0b'
                        }}
                      >
                        {hasImage ? (
                          <Box
                            className="media"
                            component="img"
                            src={project.image}
                            alt={p.title}
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              objectPosition: 'top center',
                              transition: 'transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)'
                            }}
                          />
                        ) : isYouTubeEmbed ? (
                          <Box
                            component="iframe"
                            src={`${p.src}${p.src.includes('?') ? '&' : '?'}rel=0&modestbranding=1`}
                            title={p.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              border: 0
                            }}
                          />
                        ) : (
                          <>
                            <Box
                              className="media"
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                transition: 'transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
                                background: 'linear-gradient(135deg, #1e293b 0%, #0a0a0b 60%)'
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                opacity: 0.7,
                                background: 'radial-gradient(circle at 25% 20%, rgba(37, 99, 235, 0.5) 0%, transparent 55%), radial-gradient(circle at 75% 80%, rgba(99, 102, 241, 0.45) 0%, transparent 55%)'
                              }}
                            />
                            <Stack
                              alignItems="center"
                              justifyContent="center"
                              sx={{ position: 'absolute', inset: 0 }}
                            >
                              <Box
                                sx={{
                                  width: 52,
                                  height: 52,
                                  borderRadius: '50%',
                                  background: 'rgba(255,255,255,0.08)',
                                  backdropFilter: 'blur(10px)',
                                  border: '1px solid rgba(255,255,255,0.18)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Box
                                  component="svg"
                                  viewBox="0 0 24 24"
                                  sx={{ width: 20, height: 20, color: '#ffffff', ml: 0.4 }}
                                >
                                  <path d="M8 5v14l11-7z" fill="currentColor" />
                                </Box>
                              </Box>
                            </Stack>
                          </>
                        )}
                      </Box>

                      {/* Card body */}
                      <Stack
                        spacing={1.5}
                        sx={{
                          p: { xs: 2.5, md: 3 },
                          flex: 1
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: { xs: 18, md: 20 },
                            fontWeight: 600,
                            letterSpacing: '-0.02em',
                            lineHeight: 1.2,
                            color: '#0a0a0b'
                          }}
                        >
                          {p.title}
                        </Typography>

                        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                          {tags.map((tag, idx) => (
                            <Box
                              key={tag}
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 999,
                                background: idx === 0
                                  ? 'rgba(37, 99, 235, 0.08)'
                                  : 'rgba(168, 85, 247, 0.08)',
                                border: '1px solid',
                                borderColor: idx === 0
                                  ? 'rgba(37, 99, 235, 0.2)'
                                  : 'rgba(168, 85, 247, 0.2)'
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: '"Roboto Mono", monospace',
                                  fontSize: 9.5,
                                  fontWeight: 500,
                                  letterSpacing: 0.6,
                                  textTransform: 'uppercase',
                                  color: idx === 0 ? '#1d4ed8' : '#7e22ce'
                                }}
                              >
                                {tag}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>

                        <Typography
                          sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: 13.5,
                            fontWeight: 400,
                            lineHeight: 1.55,
                            color: '#52525b',
                            flex: 1
                          }}
                        >
                          {project.description || 'A recent piece of work — open to view.'}
                        </Typography>

                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.75}
                          sx={{ pt: 0.5 }}
                        >
                          <Typography
                            className="visit-link"
                            sx={{
                              fontFamily: '"Inter", sans-serif',
                              fontSize: 13,
                              fontWeight: 600,
                              letterSpacing: '-0.005em',
                              color: '#3f3f46',
                              transition: 'color 0.25s ease'
                            }}
                          >
                            {p.category === 'Websites' ? 'Visit site' : 'Watch'}
                          </Typography>
                          <Box
                            className="visit-arrow"
                            component="svg"
                            viewBox="0 0 24 24"
                            sx={{
                              width: 14,
                              height: 14,
                              color: '#3f3f46',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <path
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M7 17 L17 7 M9 7 L17 7 L17 15"
                            />
                          </Box>
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            </Stack>
            </Reveal>
          </Container>
        </Box>

        {/* Final CTA */}
        <Box sx={{ ...sectionShellSx, py: { xs: 14, md: 20 } }}>
          <Container maxWidth="lg">
            <Reveal>
            <Box
              sx={{
                position: 'relative',
                borderRadius: 4,
                overflow: 'hidden',
                background: '#0a0a0b',
                color: '#ffffff',
                px: { xs: 4, md: 8 },
                py: { xs: 7, md: 10 }
              }}
            >
              {/* Soft purple glow */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '-30%',
                  right: '-10%',
                  width: 600,
                  height: 600,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(37, 99, 235, 0.4) 0%, transparent 65%)',
                  filter: 'blur(70px)',
                  pointerEvents: 'none'
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '-30%',
                  left: '-10%',
                  width: 500,
                  height: 500,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 65%)',
                  filter: 'blur(80px)',
                  pointerEvents: 'none'
                }}
              />

              <Stack
                spacing={5}
                alignItems="flex-start"
                sx={{ position: 'relative', zIndex: 1 }}
              >
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Box sx={{ width: 24, height: 1, background: 'rgba(255,255,255,0.5)' }} />
                  <Typography
                    sx={{
                      fontFamily: '"Roboto Mono", monospace',
                      fontSize: 11,
                      fontWeight: 500,
                      color: 'rgba(255,255,255,0.8)',
                      letterSpacing: 1.4,
                      textTransform: 'uppercase'
                    }}
                  >
                    Get In Touch
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: { xs: '44px', md: '76px' },
                    fontWeight: 700,
                    lineHeight: 1.04,
                    letterSpacing: '-0.04em',
                    color: '#ffffff',
                    maxWidth: 880
                  }}
                >
                  Let&apos;s make something{' '}
                  <Box component="span" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                    worth watching.
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: { xs: 16, md: 18 },
                    fontWeight: 400,
                    lineHeight: 1.5,
                    color: 'rgba(255,255,255,0.6)',
                    maxWidth: 520
                  }}
                >
                  Tell us about your project. We&apos;ll get back within one business day.
                </Typography>

                <Stack direction="row" spacing={1.5}>
                  <Button
                    component={RouterLink}
                    href={paths.contact}
                    endIcon={(
                      <SvgIcon sx={{ fontSize: 16 }}>
                        <ArrowRightIcon />
                      </SvgIcon>
                    )}
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      background: '#ffffff',
                      color: '#0a0a0b',
                      fontWeight: 500,
                      fontSize: 15,
                      letterSpacing: '-0.01em',
                      px: 2.75,
                      py: 1.5,
                      borderRadius: 999,
                      textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': { background: '#f4f4f5', boxShadow: 'none' }
                    }}
                    variant="contained"
                  >
                    Start your project
                  </Button>
                  <Button
                    component={RouterLink}
                    href={paths.contact}
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      color: '#ffffff',
                      fontWeight: 500,
                      fontSize: 15,
                      letterSpacing: '-0.01em',
                      px: 2.75,
                      py: 1.5,
                      borderRadius: 999,
                      textTransform: 'none',
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'transparent',
                      '&:hover': { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.4)' }
                    }}
                    variant="outlined"
                  >
                    Email us
                  </Button>
                </Stack>
              </Stack>
            </Box>
            </Reveal>
          </Container>
        </Box>

        {/* FAQ */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="md">
            <Reveal>
            <Stack spacing={{ xs: 5, md: 7 }} alignItems="center">
              <Stack spacing={2.5} alignItems="center">
                <SectionLabel>Common Questions</SectionLabel>
                <Typography
                  align="center"
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: { xs: '36px', md: '60px' },
                    fontWeight: 700,
                    lineHeight: 1.04,
                    letterSpacing: '-0.035em',
                    color: '#0a0a0b'
                  }}
                >
                  Frequently Asked{' '}
                  <Box component="span" sx={{ color: '#2563eb' }}>Questions</Box>
                </Typography>
                <Typography
                  align="center"
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: { xs: 15, md: 17 },
                    fontWeight: 400,
                    lineHeight: 1.5,
                    color: '#52525b',
                    maxWidth: 560
                  }}
                >
                  Everything you need to know about Mystic Media Film and how we work.
                </Typography>
              </Stack>

              <Stack spacing={1.5} sx={{ width: '100%', maxWidth: 720 }}>
                {faqs.map((f, i) => (
                  <FaqItem
                    key={f.q}
                    q={f.q}
                    a={f.a}
                    i={i}
                    isOpen={openFaq === i}
                    onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                  />
                ))}
              </Stack>
            </Stack>
            </Reveal>
          </Container>
        </Box>

        {/* Google Reviews */}
        <HomeGoogleReviews />

      </main>
    </>
  );
};

export default Page;
